// apps/web/src/components/painel/home/assinatura-em-poder.tsx — Fase 17.8, Plano 16 (D-12; UI-SPEC U-18, U-24).
// Assinatura "Em poder da Pieltec · peças de clientes" do Faturamento: até 4 clientes (o botão "Ver todos" expande no próprio painel
// até o que a API devolveu), cada um com o nome (quebra sem reticências), o total em pç e uma barra de 8px com dois segmentos lado a
// lado: em produção (gradiente do perfil) e pronto para devolver (token emerald, nunca âmbar: âmbar é só alerta). A largura de cada
// segmento é proporcional ao maior total da lista. Sem peças em poder: uma linha amigável com Package. Nenhum valor em R$.
import { useId, useState, type CSSProperties, type ReactElement } from 'react';
import { Package } from 'lucide-react';
import type { ClienteEmPoder, Cobertura } from '@pieltec/contracts';
import { formatarNumero } from '../../../lib/painel/home/formato';
import { EstadoVazio } from './estado-vazio';
import '../../../styles/painel-home.css';

export const TEXTO_SEM_PECAS_EM_PODER = 'Nenhuma peça de cliente em poder da Pieltec.';

/** Linhas visíveis antes de "Ver todos". */
export const LINHAS_VISIVEIS_EM_PODER = 4;

export interface AssinaturaEmPoderProps {
  readonly clientes: readonly ClienteEmPoder[];
  /** Todos os clientes com peças em poder (pode ser maior que `clientes.length`). */
  readonly totalClientes: number;
  readonly cobertura: Cobertura;
}

export interface AssinaturaEmPoderVisaoProps extends AssinaturaEmPoderProps {
  readonly expandido: boolean;
  readonly onAlternar: () => void;
}

/** Larguras (% com 1 casa) dos dois segmentos de um cliente, proporcionais ao maior total da lista; maior total inválido não gera largura. */
export function larguraDosSegmentos(cliente: ClienteEmPoder, maiorTotal: number): { readonly producao: number; readonly pronto: number } {
  if (!Number.isFinite(maiorTotal) || maiorTotal <= 0) return { producao: 0, pronto: 0 };
  const percentual = (valor: number): number => (valor > 0 ? Math.round((valor / maiorTotal) * 1000) / 10 : 0);
  return { producao: percentual(cliente.emProducao), pronto: percentual(cliente.prontoParaDevolver) };
}

const pecas = (valor: number): string => `${formatarNumero(valor)} pç`;

function Linha({ cliente, maiorTotal }: { readonly cliente: ClienteEmPoder; readonly maiorTotal: number }): ReactElement {
  const total = cliente.emProducao + cliente.prontoParaDevolver;
  const largura = larguraDosSegmentos(cliente, maiorTotal);
  // A barra ocupa a proporção do total sobre o maior total da lista; dentro dela os dois segmentos dividem o espaço (menos o gap de 4px)
  // na proporção das peças, então a soma nunca passa da largura da linha.
  const estilo = { '--w-total': `${Math.min(100, Math.round((largura.producao + largura.pronto) * 10) / 10)}%`, '--g-producao': cliente.emProducao, '--g-pronto': cliente.prontoParaDevolver } as CSSProperties;
  return (
    <li className="painel-em-poder__linha" data-testid="painel-em-poder-linha">
      <div className="painel-em-poder__topo">
        <span className="painel-em-poder__nome">{cliente.nome}</span>
        <span className="painel-em-poder__total">{pecas(total)}</span>
      </div>
      <div
        className="painel-em-poder__barra"
        style={estilo}
        role="img"
        aria-label={`${cliente.nome}: ${pecas(cliente.emProducao)} em produção e ${pecas(cliente.prontoParaDevolver)} prontas para devolver`}
      >
        {cliente.emProducao > 0 && <span className="painel-em-poder__segmento painel-em-poder__segmento--producao" />}
        {cliente.prontoParaDevolver > 0 && <span className="painel-em-poder__segmento painel-em-poder__segmento--pronto" />}
      </div>
    </li>
  );
}

/** Visão controlada (testável por SSR): `AssinaturaEmPoder` só guarda expandido/recolhido. */
export function AssinaturaEmPoderVisao({ clientes, totalClientes, cobertura, expandido, onAlternar }: AssinaturaEmPoderVisaoProps): ReactElement {
  const idTitulo = useId();
  const idLista = useId();
  const visiveis = expandido ? clientes : clientes.slice(0, LINHAS_VISIVEIS_EM_PODER);
  const maiorTotal = Math.max(0, ...clientes.map((cliente) => cliente.emProducao + cliente.prontoParaDevolver));
  const temMais = totalClientes > LINHAS_VISIVEIS_EM_PODER || clientes.length > LINHAS_VISIVEIS_EM_PODER;
  const coberturaParcial = cobertura.total > 0 && cobertura.base < cobertura.total;
  return (
    <section className="painel-assinatura" data-testid="painel-assinatura" data-tipo="em-poder" aria-labelledby={idTitulo}>
      <header className="painel-assinatura__cabecalho">
        <h3 id={idTitulo} className="painel-assinatura__titulo">
          Em poder da Pieltec
        </h3>
        <span className="painel-assinatura__subtitulo">peças de clientes</span>
      </header>
      {clientes.length === 0 ? (
        <EstadoVazio icone={Package} texto={TEXTO_SEM_PECAS_EM_PODER} />
      ) : (
        <>
          <ul id={idLista} className="painel-em-poder">
            {visiveis.map((cliente) => (
              <Linha key={cliente.clienteId} cliente={cliente} maiorTotal={maiorTotal} />
            ))}
          </ul>
          <div className="painel-em-poder__legenda">
            <span className="painel-em-poder__chave">
              <span className="painel-em-poder__quadrado painel-em-poder__quadrado--producao" aria-hidden="true" />
              em produção
            </span>
            <span className="painel-em-poder__chave painel-em-poder__chave--pronto">
              <span className="painel-em-poder__quadrado painel-em-poder__quadrado--pronto" aria-hidden="true" />
              pronto para devolver
            </span>
          </div>
          {temMais && (
            <button type="button" className="painel-em-poder__ver-todos" aria-expanded={expandido} aria-controls={idLista} onClick={onAlternar} data-testid="painel-em-poder-ver-todos">
              {expandido ? 'Mostrar menos' : `Ver todos (${formatarNumero(totalClientes)})`}
            </button>
          )}
          {expandido && totalClientes > clientes.length && (
            <p className="painel-em-poder__nota">{`Mostrando os ${formatarNumero(clientes.length)} maiores de ${formatarNumero(totalClientes)}.`}</p>
          )}
          {coberturaParcial && <p className="painel-em-poder__nota">{`${formatarNumero(cobertura.base)} de ${formatarNumero(cobertura.total)} lotes com saldo entram nesta soma.`}</p>}
        </>
      )}
    </section>
  );
}

export function AssinaturaEmPoder(props: AssinaturaEmPoderProps): ReactElement {
  const [expandido, setExpandido] = useState(false);
  return <AssinaturaEmPoderVisao {...props} expandido={expandido} onAlternar={() => setExpandido((atual) => !atual)} />;
}
